import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { getService, getFormFields } from '../api';
import { useSettings } from '../context/SettingsContext';
import { waLink, fillTemplate } from '../components/WhatsAppFloat';
import PageWrapper from '../components/PageWrapper';
import Loader from '../components/Loader';
import ShareButtons from '../components/ShareButtons';
import DynamicForm from '../components/DynamicForm';
import Reveal from '../components/Reveal';

export default function ServiceDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    setLoading(true);
    getService(slug).then(setService).catch(() => setService(null)).finally(() => setLoading(false));
    getFormFields(true).then(setFields).catch(() => {});
  }, [slug]);

  if (loading) return <PageWrapper><Loader /></PageWrapper>;
  if (!service) {
    return (
      <PageWrapper>
        <div className="empty" style={{ minHeight: '60vh' }}>
          <div className="empty__icon">😕</div>
          <h3>Service not found</h3>
          <Link to="/services" className="btn btn-ghost mt-2">Back to services</Link>
        </div>
      </PageWrapper>
    );
  }

  // Message template is authored by the admin (Settings → WhatsApp).
  const wa = waLink(
    settings?.whatsappNumber,
    fillTemplate(settings?.whatsappServiceMessage || 'Hi! I would like to know more about your "{service}" service. {url}', {
      service: service.title,
      url: window.location.href,
      site: settings?.siteName || '',
    })
  );

  return (
    <PageWrapper>
      {/* Header */}
      <section className="section detail" style={{ paddingBottom: 30 }}>
        <div className="container">
          <Link to="/services" className="muted" style={{ fontSize: 14 }}>← Back to services</Link>
          <div className="flex center gap" style={{ marginTop: 18 }}>
            <div className="scard__icon" style={{ margin: 0 }}>{service.icon || '✨'}</div>
            <div>
              <h1 className="detail__title" style={{ margin: 0 }}>{service.title}</h1>
              <p className="muted">{service.shortDescription}</p>
            </div>
          </div>
        </div>
      </section>

      {service.image && (
        <section className="container" style={{ marginBottom: 10 }}>
          <Reveal className="gallery__main" style={{ aspectRatio: '21/8' }}>
            <img src={service.image} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Reveal>
        </section>
      )}

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="container grid grid-2" style={{ alignItems: 'start', gap: 40 }}>
          <div>
            <h2 className="section-title" style={{ fontSize: 26 }}>Overview</h2>
            <p className="prose">{service.description}</p>

            {service.tags?.length > 0 && (
              <>
                <div className="detail__section-h">Tags</div>
                <div className="flex gap wrap">
                  {service.tags.map((t) => <span key={t} className="tag-chip">#{t}</span>)}
                </div>
              </>
            )}

            <div className="detail__section-h">Share</div>
            <ShareButtons title={service.title} />
          </div>

          {/* Enquiry */}
          <div className="glass" style={{ padding: 26, position: 'sticky', top: 90 }}>
            <h3 style={{ marginBottom: 6 }}>Interested in this service?</h3>
            <p className="muted" style={{ fontSize: 14, marginBottom: 18 }}>Send us your details and we'll reach out.</p>
            <DynamicForm fields={fields} source="service" refId={service._id} refName={service.title} single />
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer" className="btn btn-block mt-2" style={{ background: 'var(--whatsapp)', color: '#fff' }}>
                <FaWhatsapp /> Chat on WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
